import * as THREE from 'three';
import { ARCHER } from '../../models/siege.ts';
import { clip, type Bones, type Clip } from '../clip.ts';
import { TAU, inCubic, inOut, inQuad, keys, lerp, outBack, outCubic, outExpo, outQuad, phase, settle, span, type Key } from '../curves.ts';
import { apply, timeline, type KeyPose } from '../pose.ts';

/*
 * Era II nightmares (sapper, bone ram, spectral archer) and Halloween's cursed pumpkin. Every one has idle
 * and walk (loops), attack (the blow or shot lands at ~0.13 s, when the simulation deals it), hit, stun (loop)
 * and die (ends on the ground; the view then sinks it). The sapper also burrows and emerges.
 */

/* ---------- sapper: a squat miner swinging a two-handed pick ---------- */

type SB = 'root' | 'body' | 'head' | 'arms' | 'pick' | 'sack' | 'lantern' | 'legL' | 'shinL' | 'legR' | 'shinR';
type SP = KeyPose<SB>;

/** Hunched over the pick, knees bent a touch more than the model. */
const S_STANCE: SP = { body: [0.05, 0, 0], legL: [-0.06, 0, 0], shinL: [0.1, 0, 0], legR: [-0.06, 0, 0], shinR: [0.1, 0, 0], root: [0, 0, 0, 0, -0.01, 0] };

const sapperIdle = (): Clip<SB> =>
  clip<SB>('idle', 2.8, true, (b, t) => {
    const p = phase(t, 2.8) * TAU;
    apply(b, S_STANCE);
    const br = Math.sin(p * 2);
    b.root.pos(0, -0.008 * (1 + br), 0);
    b.body.rot(0.035 * br, 0.07 * Math.sin(p), 0.025 * Math.sin(p + 1));
    // It sniffs the ground twice a loop, quick dips held a beat, and peers about.
    const sniff = Math.max(0, Math.sin(p * 2 - 0.6)) ** 8;
    b.head.rot(-0.04 * br + 0.16 * sniff, 0.28 * Math.sin(p + 0.7) * Math.max(0, Math.sin(p + 0.7)), 0.06 * Math.sin(p));
    b.arms.rot(0.04 * Math.sin(p * 2 - 0.4));
    b.pick.rot(0.04 * Math.sin(p * 2 - 0.9));
    // The sack and the lantern hang a beat behind the body.
    b.sack.rot(0.06 * Math.sin(p * 2 - 1.2), 0, 0.05 * Math.sin(p - 0.8));
    b.lantern.rot(0.05 * Math.sin(p * 2 - 1.5), 0, 0.1 * Math.sin(p - 1.1));
    b.legL.rot(0, 0, -0.02 * Math.sin(p));
    b.legR.rot(0, 0, -0.02 * Math.sin(p));
  }, (t) => {
    const br = Math.sin(phase(t, 2.8) * TAU * 2);
    return [1 + 0.015 * br, 1 - 0.02 * br, 1 + 0.015 * br];
  });

/** A short-legged waddle: the pick bobs on its shoulder, sack and lantern swing behind. */
const sapperWalk = (dur = 0.6): Clip<SB> =>
  clip<SB>('walk', dur, true, (b, t) => {
    const th = phase(t, dur) * TAU;
    const sn = Math.sin(th), cs = Math.cos(th);
    apply(b, S_STANCE);
    b.legL.rot(-0.46 * sn, 0, -0.03 * cs);
    b.legR.rot(0.46 * sn, 0, 0.03 * cs);
    b.shinL.rot(0.85 * Math.max(0, cs) ** 1.4);
    b.shinR.rot(0.85 * Math.max(0, -cs) ** 1.4);
    b.root.pos(0, 0.022 * Math.cos(2 * th) + 0.006, 0).rot(0, 0.08 * sn, 0.07 * Math.sin(th - 0.4));
    b.body.rot(0.12 + 0.04 * Math.cos(2 * th - 0.5), -0.12 * sn, -0.05 * sn);
    b.head.rot(-0.08 - 0.05 * Math.cos(2 * th - 1.0), 0.1 * sn, -0.05 * sn);
    b.arms.rot(0.08 * Math.cos(2 * th - 0.8), 0, 0.03 * sn);
    b.pick.rot(0.07 * Math.cos(2 * th - 1.4));
    b.sack.rot(0.05 + 0.14 * Math.cos(2 * th - 1.7), 0, -0.12 * Math.sin(th - 0.9));
    b.lantern.rot(0.12 + 0.12 * Math.cos(2 * th - 2.0), 0, -0.3 * Math.sin(th - 1.3));
  }, (t) => {
    const k = Math.cos(phase(t, dur) * TAU * 2);
    return [1 - 0.025 * k, 1 + 0.035 * k, 1 - 0.025 * k];
  });

/** Pick raised high behind the head, then brought down with the whole body behind it; it bites at 0.14 s. */
const S_UP: SP = { ...S_STANCE, arms: [-1.35, 0, 0.05], pick: [-0.3, 0, 0], body: [-0.22, 0.12, 0], head: [-0.3, 0, 0], legL: [-0.18, 0, 0], legR: [0.1, 0, 0], shinL: [0.15, 0, 0], root: [0, 0, 0, 0, 0.02, -0.03], sack: [0.15, 0, 0] };
const S_DOWN: SP = { body: [0.5, -0.08, 0], head: [0.25, 0, 0], arms: [0.5, 0, -0.05], pick: [1.35, 0, 0], legL: [-0.5, 0, 0], shinL: [0.45, 0, 0], legR: [0.22, 0, 0], shinR: [0.25, 0, 0], root: [0, 0, 0, 0, -0.07, 0.09], sack: [-0.2, 0, 0], lantern: [-0.25, 0, 0] };

const sapperAttack = (): Clip<SB> =>
  clip<SB>('attack', 0.66, false, (b, t) => {
    timeline(b, t, [
      [0, S_STANCE],
      [0.075, S_UP, outQuad],
      [0.135, S_DOWN, inCubic],
      [0.3, { ...S_DOWN, body: [0.46, -0.08, 0], arms: [0.46, 0, -0.05], pick: [1.3, 0, 0], sack: [0.1, 0, 0], lantern: [0.15, 0, 0] }, outBack],
      // It wrenches the pick out of the ground.
      [0.42, { ...S_DOWN, body: [0.25, 0.05, 0], head: [0.05, 0, 0], arms: [0.05, 0, 0], pick: [0.7, 0, 0], root: [0, 0, 0, 0, -0.04, 0.05], sack: [0.05, 0, 0], lantern: [0, 0, 0] }, inOut],
      [0.66, S_STANCE, inOut],
    ], { pick: 0.012, head: 0.02, sack: 0.05, lantern: 0.06 });
    // The shock up the haft.
    if (t > 0.135) {
      const k = settle(t - 0.135, 6, 12);
      b.arms.rot(-0.06 * k);
      b.body.rot(0.04 * k);
    }
  }, (t) => {
    const k = t > 0.135 ? Math.max(0, settle(t - 0.135, 3, 10)) : 0;
    return [1 + 0.05 * k, 1 - 0.06 * k, 1 + 0.05 * k];
  });

const sapperHit = (): Clip<SB> =>
  clip<SB>('hit', 0.38, false, (b, t) => {
    apply(b, S_STANCE);
    const k = settle(t, 2.4, 8) * span(t, 0, 0.03, outQuad);
    const a = Math.abs(k);
    b.body.rot(-0.4 * k, 0.15 * k, 0.08 * k);
    b.head.rot(-0.45 * k, -0.2 * k);
    b.arms.rot(-0.3 * k);
    b.pick.rot(-0.2 * k);
    b.root.pos(0, -0.02 * a, -0.08 * k);
    b.shinL.rot(0.2 * a);
    b.shinR.rot(0.25 * a);
    const lag = settle(t - 0.04, 2.2, 6);
    b.sack.rot(0.35 * lag);
    b.lantern.rot(0.3 * lag, 0, 0.2 * lag);
  }, (t) => {
    const k = Math.max(0, settle(t, 3, 9));
    return [1 + 0.1 * k, 1 - 0.12 * k, 1 + 0.1 * k];
  });

/** Dazed: it reels, the pick sagging to the ground, head lolling. */
const sapperStun = (): Clip<SB> =>
  clip<SB>('stun', 1.4, true, (b, t) => {
    const p = phase(t, 1.4) * TAU;
    apply(b, S_STANCE, 0.6);
    b.root.rot(0.05 * Math.sin(p), 0, 0.1 * Math.cos(p));
    b.body.rot(0.22 + 0.07 * Math.sin(p * 2), 0, -0.1 * Math.cos(p));
    b.head.rot(0.25 * Math.sin(p + 0.8), 0.3 * Math.cos(p + 0.8), 0.25 * Math.sin(p + 0.8));
    b.arms.rot(0.35 + 0.08 * Math.sin(p + 1.2));
    b.pick.rot(0.75 + 0.1 * Math.sin(p + 1.6));
    b.sack.rot(-0.1 + 0.12 * Math.sin(p + 2), 0, 0.15 * Math.cos(p + 2));
    b.lantern.rot(0.1 * Math.sin(p + 2.2), 0, -0.25 * Math.cos(p + 2.2));
  });

/** Struck dead: it jerks back, drops to its knees, the pick slips, and it pitches onto its face. */
const sapperDie = (): Clip<SB> =>
  clip<SB>('die', 1.0, false, (b, t) => {
    const flat: SP = { body: [0.1, 0, 0.1], head: [-0.75, 0.6, 0.25], arms: [-1.9, 0, 0.25], pick: [1.7, 0, 0.1], legL: [-0.4, 0, -0.15], shinL: [0.6, 0, 0], legR: [-0.25, 0, 0.15], shinR: [0.7, 0, 0], sack: [-1.2, 0, 0.4], lantern: [-1.5, 0, 0.5], root: [1.35, 0, 0, 0, 0.27, 0.05] };
    timeline(b, t, [
      [0, S_STANCE],
      [0.1, { ...S_STANCE, body: [-0.5, 0.1, 0.1], head: [-0.6, 0.2, 0], arms: [-0.9, 0, 0.1], pick: [-0.4, 0, 0], root: [0, 0, 0, 0, 0.02, -0.05], sack: [0.4, 0, 0] }, outExpo],
      [0.4, { body: [0.3, 0, 0.05], head: [0.35, 0.3, 0], arms: [0.25, 0, 0], pick: [0.5, 0, 0], legL: [-1.35, 0, -0.2], shinL: [2.0, 0, 0], legR: [-1.25, 0, 0.2], shinR: [2.0, 0, 0], root: [0, 0, 0, 0, -0.22, 0], sack: [0.2, 0, 0] }, inQuad],
      [0.7, flat, inQuad],
      [1.0, { ...flat, root: [1.4, 0, 0, 0, 0.27, 0.06] }, outBack],
    ], { head: 0.03, pick: 0.04, sack: 0.06, lantern: 0.08 });
  });

/** It drives the pick into the ground and dives in head first, legs kicking, until it is gone. */
const sapperBurrow = (): Clip<SB> =>
  clip<SB>('burrow', 0.85, false, (b, t) => {
    timeline(b, t, [
      [0, S_STANCE],
      [0.08, S_UP, outQuad],
      [0.15, S_DOWN, inCubic],
      [0.32, { body: [0.9, 0, 0], head: [0.45, 0, 0], arms: [0.8, 0, 0], pick: [1.6, 0, 0], legL: [0.3, 0, 0], shinL: [0.5, 0, 0], legR: [0.45, 0, 0], shinR: [0.6, 0, 0], sack: [-0.4, 0, 0], lantern: [-0.6, 0, 0], root: [0.45, 0, 0, 0, -0.32, 0.16] }, inQuad],
      [0.58, { body: [1.0, 0, 0], head: [0.5, 0, 0], arms: [1.0, 0, 0], pick: [1.7, 0, 0], legL: [0.6, 0, 0], shinL: [0.8, 0, 0], legR: [0.3, 0, 0], shinR: [0.4, 0, 0], sack: [-0.6, 0, 0], lantern: [-0.9, 0, 0], root: [0.85, 0, 0, 0, -0.9, 0.2, 0.92] }, inQuad],
      [0.85, { body: [1.0, 0, 0], head: [0.5, 0, 0], arms: [1.0, 0, 0], pick: [1.7, 0, 0], legL: [0.7, 0, 0], shinL: [0.9, 0, 0], legR: [0.7, 0, 0], shinR: [0.9, 0, 0], sack: [-0.6, 0, 0], lantern: [-0.9, 0, 0], root: [1.1, 0, 0, 0, -1.4, 0.2, 0.8] }, inQuad],
    ], { pick: 0.012, head: 0.02, sack: 0.05, lantern: 0.06 });
    // Legs scrabbling as it goes under.
    const kick = span(t, 0.3, 0.4) * (1 - span(t, 0.75, 0.85));
    b.legL.rot(0.35 * kick * Math.sin(t * 38));
    b.legR.rot(-0.35 * kick * Math.sin(t * 38));
  }, (t) => {
    const k = span(t, 0.15, 0.35) * (1 - span(t, 0.6, 0.85));
    return [1 - 0.05 * k, 1 + 0.08 * k, 1 - 0.05 * k];
  }, 'Excavar');

/** It bursts up out of the earth, pick first, lands in a crouch and shakes the dirt off. */
const sapperEmerge = (): Clip<SB> =>
  clip<SB>('emerge', 0.85, false, (b, t) => {
    const crouch: SP = { body: [0.4, 0, 0], head: [0.15, 0, 0], arms: [0.25, 0, 0], pick: [0.4, 0, 0], legL: [-0.55, 0, -0.05], shinL: [0.85, 0, 0], legR: [-0.5, 0, 0.05], shinR: [0.8, 0, 0], sack: [0.3, 0, 0], lantern: [0.3, 0, 0], root: [0, 0, 0, 0, -0.12, 0] };
    timeline(b, t, [
      [0, { body: [0.15, 0, 0], head: [-0.3, 0, 0], arms: [-1.5, 0, 0], pick: [-0.1, 0, 0], legL: [0.2, 0, 0], legR: [0.2, 0, 0], sack: [-0.3, 0, 0], root: [-0.15, 0, 0, 0, -1.25, 0, 0.9] }],
      [0.24, { body: [-0.2, 0, 0], head: [-0.35, 0, 0], arms: [-1.1, 0, 0], pick: [-0.2, 0, 0], legL: [-0.85, 0, 0], shinL: [1.4, 0, 0], legR: [-0.75, 0, 0], shinR: [1.3, 0, 0], sack: [-0.4, 0, 0], lantern: [-0.5, 0, 0], root: [0, 0, 0, 0, 0.16, 0.05] }, outCubic],
      [0.4, crouch, inQuad],
      [0.6, { ...crouch, body: [0.2, 0, 0], root: [0, 0, 0, 0, -0.05, 0] }, outQuad],
      [0.85, S_STANCE, inOut],
    ], { head: 0.025, pick: 0.02, sack: 0.06, lantern: 0.07 });
    // The shake: a shudder through the shoulders and the head.
    const sh = span(t, 0.44, 0.5) * (1 - span(t, 0.66, 0.78));
    b.body.rot(0, 0, 0.12 * sh * Math.sin(t * 52));
    b.head.rot(0, 0.3 * sh * Math.sin(t * 52 + 1), 0.15 * sh * Math.sin(t * 52 + 0.5));
  }, (t) => {
    const k = t > 0.4 ? Math.max(0, settle(t - 0.4, 3, 9)) : 0;
    const up = span(t, 0.05, 0.2) * (1 - span(t, 0.24, 0.36));
    return [1 + 0.1 * k - 0.04 * up, 1 - 0.12 * k + 0.08 * up, 1 + 0.1 * k - 0.04 * up];
  }, 'Emerger');

export const sapperClips = (): Clip[] => [sapperIdle(), sapperWalk(), sapperAttack(), sapperHit(), sapperStun(), sapperDie(), sapperBurrow(), sapperEmerge()] as Clip[];

/* ---------- ram: a femur battering ram rolling on bone wheels ---------- */

type RB = 'root' | 'frame' | 'wheelFL' | 'wheelFR' | 'wheelBL' | 'wheelBR' | 'chainF' | 'chainB' | 'ram' | 'jaw' | 'flapL' | 'flapR';

/** Chain length (frame units): the femur hangs this far under the chain tops. */
const CHAIN = 0.26;
/** Wheel radius in root units (0.27 in the 1.3× frame): a root move of `dz` rolls the wheels `dz / WHEEL_R`. */
const WHEEL_R = 0.27 * 1.3;

/** Swings the femur on its two parallel chains by `th` (+ back, − forward): it slides along the arc, level. */
const swing = (b: Bones<RB>, th: number, tilt = 0): void => {
  b.chainF.rot(th);
  b.chainB.rot(th);
  b.ram.pos(0, CHAIN * (1 - Math.cos(th)), -CHAIN * Math.sin(th)).rot(tilt);
};

const roll = (b: Bones<RB>, a: number): void => {
  b.wheelFL.rot(a);
  b.wheelFR.rot(a);
  b.wheelBL.rot(a);
  b.wheelBR.rot(a);
};

/** The flaps: `fwd` swings them forward/back, `out` lifts them away from the sides. */
const flaps = (b: Bones<RB>, fwd: number, out: number, offR = 0): void => {
  b.flapL.rot(fwd, 0, -out);
  b.flapR.rot(fwd + offR, 0, out);
};

/** Idling like a penned bull: it inches back and forth on its wheels, the femur swaying, the jaw clacking. */
const ramIdle = (): Clip<RB> =>
  clip<RB>('idle', 3.2, true, (b, t) => {
    const p = phase(t, 3.2) * TAU;
    const dz = 0.018 * Math.sin(p);
    b.root.pos(0, 0, dz);
    roll(b, dz / WHEEL_R);
    b.frame.rot(0.012 * Math.sin(p * 2 + 0.3) - 0.01 * Math.cos(p), 0, 0.008 * Math.sin(p)).pos(0, 0.004 * Math.sin(p * 2), 0);
    swing(b, -0.12 * Math.sin(p - 0.9), 0.02 * Math.sin(p - 1.4));
    b.jaw.rot(0.04 + 0.22 * Math.max(0, Math.sin(p * 2 - 1)) ** 6);
    flaps(b, 0.05 * Math.sin(p * 2 + 1), 0.03 + 0.03 * Math.sin(p + 0.5), 0.02 * Math.sin(p * 2));
  });

/** Rolling: one turn of the wheels a cycle, the frame jolting on their knobs, the femur swinging under it. */
const ramWalk = (dur = 1.6): Clip<RB> =>
  clip<RB>('walk', dur, true, (b, t) => {
    const ph = phase(t, dur);
    const th = ph * TAU;
    roll(b, th);
    // Seven knobs a turn: the front and back wheels bite out of step and the frame pitches.
    b.frame.rot(0.018 * Math.sin(2 * th) + 0.006 * Math.sin(7 * th + 1), 0, 0.012 * Math.sin(th)).pos(0, 0.006 * Math.cos(7 * th), 0);
    b.root.pos(0, 0.003 * Math.cos(7 * th + 0.5), 0);
    swing(b, -0.16 * Math.sin(2 * th - 1.2), 0.03 * Math.sin(2 * th - 1.8));
    b.jaw.rot(0.07 + 0.05 * Math.sin(4 * th - 1));
    flaps(b, 0.12 * Math.sin(2 * th - 1.6) + 0.06, 0.04 + 0.04 * Math.sin(2 * th - 2.2), 0.04 * Math.sin(th));
  });

/** Draws the femur back, then rams it home with the whole carriage lurching behind it; it strikes at 0.13 s. */
const RAM_SWING: Key[] = [[0, 0], [0.07, 0.85, outQuad], [0.13, -1.0, inCubic], [0.2, -0.72, outQuad], [0.34, -0.2, inOut], [0.5, 0.16, inOut], [0.66, -0.05, inOut], [0.85, 0, inOut]];
const ramAttack = (): Clip<RB> =>
  clip<RB>('attack', 0.85, false, (b, t) => {
    const dz = keys(t, [[0, 0], [0.07, -0.07, outQuad], [0.13, 0.14, inCubic], [0.2, 0.1, outQuad], [0.45, 0.03, inOut], [0.85, 0, inOut]]);
    b.root.pos(0, 0, dz);
    roll(b, dz / WHEEL_R);
    const hit = t > 0.13 ? settle(t - 0.13, 6, 10) : 0;
    swing(b, keys(t, RAM_SWING), 0.06 * hit);
    b.frame.rot(keys(t, [[0, 0], [0.07, -0.05, outQuad], [0.13, 0.07, inCubic], [0.25, -0.03, outQuad], [0.42, 0.012], [0.85, 0, inOut]]) + 0.035 * hit).pos(0, 0.012 * Math.abs(hit), 0);
    b.jaw.rot(keys(t, [[0, 0], [0.07, 0.5, outQuad], [0.13, 0.12, inCubic], [0.2, 0.38, outQuad], [0.4, 0.06], [0.85, 0, inOut]]));
    const lagged = keys(t - 0.05, RAM_SWING);
    flaps(b, -0.35 * lagged, 0.05 + 0.1 * Math.abs(lagged), 0.05 * hit);
  }, (t) => {
    const k = t > 0.13 ? Math.max(0, settle(t - 0.13, 3, 9)) : 0;
    return [1 + 0.04 * k, 1 - 0.05 * k, 1 + 0.03 * k];
  });

const ramHit = (): Clip<RB> =>
  clip<RB>('hit', 0.42, false, (b, t) => {
    const k = settle(t, 2.5, 8) * span(t, 0, 0.03, outQuad);
    const dz = -0.05 * k;
    b.root.pos(0, 0, dz);
    roll(b, dz / WHEEL_R);
    b.frame.rot(-0.06 * k, 0, 0.03 * k).pos(0, 0.02 * Math.abs(k), 0);
    swing(b, 0.4 * settle(t - 0.03, 2, 6) * span(t, 0, 0.06));
    b.jaw.rot(0.3 * Math.abs(k));
    flaps(b, 0.25 * k, 0.15 * Math.abs(k));
  }, (t) => {
    const k = Math.max(0, settle(t, 3, 9));
    return [1 + 0.05 * k, 1 - 0.06 * k, 1 + 0.05 * k];
  });

/** Stalled: listing on its wheels, rocking back and forth, the femur swinging loose, jaw hanging. */
const ramStun = (): Clip<RB> =>
  clip<RB>('stun', 1.6, true, (b, t) => {
    const p = phase(t, 1.6) * TAU;
    const dz = 0.03 * Math.sin(p);
    b.root.pos(0, 0, dz);
    roll(b, dz / WHEEL_R);
    b.frame.rot(0.03 * Math.sin(p + 0.6), 0, 0.06 + 0.03 * Math.sin(p));
    swing(b, 0.4 * Math.sin(p - 1.2), 0.04 * Math.sin(p - 1.6));
    b.jaw.rot(0.38 + 0.08 * Math.sin(p * 2));
    flaps(b, 0.15 * Math.sin(p - 1.5), 0.02, 0.05 * Math.sin(p));
  });

/** It breaks apart: a front wheel snaps off, the carriage slams down on that corner, the femur crashes to the ground. */
const ramDie = (): Clip<RB> =>
  clip<RB>('die', 1.15, false, (b, t) => {
    const shud = settle(t, 5, 7) * span(t, 0, 0.03);
    b.root.pos(0, 0, keys(t, [[0, 0], [0.4, 0.06, outQuad], [0.7, 0.08, outQuad]]));
    // The front-left wheel tears free and falls flat outward; the rear-right one cracks and leans.
    timeline(b, t, [
      [0, {}],
      [0.15, { wheelFL: [0.15, 0, 0.25, -0.03, 0, 0], wheelBR: [0.1, 0, 0] }],
      [0.52, { wheelFL: [0.4, 0, 1.45, -0.3, -0.18, 0.04], wheelBR: [0.2, 0, -0.12, 0, -0.03, 0] }, inQuad],
      [0.64, { wheelFL: [0.4, 0, 1.3, -0.3, -0.15, 0.05], wheelBR: [0.2, 0, -0.32, 0, -0.06, 0] }, outQuad],
      [0.8, { wheelFL: [0.4, 0, 1.52, -0.31, -0.19, 0.05], wheelBR: [0.2, 0, -0.3, 0, -0.06, 0] }, inQuad],
      [1.15, { wheelFL: [0.4, 0, 1.5, -0.31, -0.185, 0.05], wheelBR: [0.2, 0, -0.3, 0, -0.06, 0] }],
    ]);
    b.wheelFR.rot(keys(t, [[0, 0], [0.5, 0.25, outQuad]]));
    b.wheelBL.rot(keys(t, [[0, 0], [0.5, 0.2, outQuad]]));
    // The carriage drops onto the broken corner, bounces once and settles nose-down.
    b.frame.rot(
      keys(t, [[0, 0], [0.18, -0.03], [0.5, 0.13, inQuad], [0.62, 0.09, outQuad], [0.78, 0.14, inQuad], [1.15, 0.135]]) + 0.03 * shud,
      keys(t, [[0, 0], [0.5, 0.05], [1.15, 0.06]]),
      keys(t, [[0, 0], [0.18, -0.02], [0.5, 0.2, inQuad], [0.62, 0.15, outQuad], [0.78, 0.21, inQuad], [1.15, 0.2]]),
    ).pos(0, keys(t, [[0, 0], [0.5, -0.1, inQuad], [0.62, -0.07, outQuad], [0.78, -0.12, inQuad], [1.15, -0.115]]), 0);
    // The chains give: the femur lurches forward, then drops and its skull strikes the ground.
    const th = keys(t, [[0, 0], [0.2, -0.3, outQuad], [0.5, -0.55, inOut], [0.82, 0.25, inQuad], [1.15, 0.3]]);
    swing(b, th, keys(t, [[0, 0], [0.55, 0], [0.82, 0.1, inQuad], [0.92, -0.04, outQuad], [1.15, -0.02]]));
    b.ram.pos(0, keys(t, [[0, 0], [0.6, 0], [0.82, -0.06, inQuad], [0.92, -0.03, outQuad], [1.15, -0.04]]), 0);
    b.jaw.rot(keys(t, [[0, 0], [0.12, 0.45, outExpo], [0.5, 0.2], [0.85, 0.36, inQuad], [1.15, 0.22]]));
    flaps(b, 0.2 * settle(t - 0.5, 2, 4) * span(t, 0.45, 0.55), keys(t, [[0, 0], [0.5, 0.2], [1.15, -0.05]]));
  }, (t) => {
    const k = t > 0.5 ? Math.max(0, settle(t - 0.5, 3, 9)) : 0;
    return [1 + 0.04 * k, 1 - 0.05 * k, 1 + 0.04 * k];
  });

export const ramClips = (): Clip[] => [ramIdle(), ramWalk(), ramAttack(), ramHit(), ramStun(), ramDie()] as Clip[];

/* ---------- mangonel: a bone mangonel that throws from afar ---------- */

type MB = 'root' | 'frame' | 'wheelFL' | 'wheelFR' | 'wheelBL' | 'wheelBR' | 'arm' | 'stone';

/** Wheel radius in root units (0.25 in the 1.25× frame). */
const M_WHEEL_R = 0.25 * 1.25;
/** The arm struck against the stop beam (rest: 0, lying back). */
const M_UP = 1.85;

const mroll = (b: Bones<MB>, a: number): void => {
  b.wheelFL.rot(a);
  b.wheelFR.rot(a);
  b.wheelBL.rot(a);
  b.wheelBR.rot(a);
};

/** Waiting loaded: the skein creaks, the arm trembles against it, the carriage settles on its wheels. */
const mangonelIdle = (): Clip<MB> =>
  clip<MB>('idle', 2.8, true, (b, t) => {
    const p = phase(t, 2.8) * TAU;
    b.frame.rot(0.008 * Math.sin(p), 0, 0.006 * Math.sin(p * 2 + 0.4)).pos(0, 0.003 * Math.sin(p * 2), 0);
    b.arm.rot(-0.02 + 0.015 * Math.sin(p * 3) + 0.01 * Math.sin(p * 7 + 1));
    b.stone.rot(0, 0.2 * Math.sin(p), 0);
  });

/** Rolling: the wheels turn, the carriage pitches on their knobs, the loaded arm bobs. */
const mangonelWalk = (dur = 1.8): Clip<MB> =>
  clip<MB>('walk', dur, true, (b, t) => {
    const th = phase(t, dur) * TAU;
    mroll(b, th);
    b.frame.rot(0.02 * Math.sin(2 * th) + 0.006 * Math.sin(7 * th + 1), 0, 0.012 * Math.sin(th)).pos(0, 0.006 * Math.cos(7 * th), 0);
    b.arm.rot(0.05 * Math.sin(2 * th - 0.8));
  });

/**
 * The throw: the arm is wound a hair further back, lets go and whips up into the stop beam at 0.3 s, the
 * stone flies off (hidden), the whole carriage bucks; then the arm is winched back down and a new stone
 * rolls into the cup.
 */
const MANGONEL_ARM: Key[] = [[0, 0], [0.18, -0.12, inOut], [0.3, M_UP, inCubic], [0.38, M_UP - 0.22, outQuad], [0.48, M_UP - 0.04, inOut], [0.7, M_UP - 0.1, inOut], [1.8, 0, inOut], [2.2, 0]];
const mangonelAttack = (): Clip<MB> =>
  clip<MB>('attack', 2.2, false, (b, t) => {
    const hit = t > 0.3 ? settle(t - 0.3, 5, 8) : 0;
    b.arm.rot(keys(t, MANGONEL_ARM));
    const dz = keys(t, [[0, 0], [0.18, 0.01], [0.3, -0.08, inCubic], [0.5, -0.03, outQuad], [0.9, 0, inOut]]);
    b.root.pos(0, 0, dz);
    mroll(b, dz / M_WHEEL_R);
    b.frame.rot(keys(t, [[0, 0], [0.18, 0.02], [0.3, -0.09, inCubic], [0.42, 0.04, outQuad], [0.6, -0.01], [0.9, 0, inOut]]) + 0.02 * hit, 0, 0.015 * hit).pos(0, 0.02 * Math.abs(hit), 0);
    b.stone.scale(t < 0.3 ? 1 : t < 1.9 ? 0.001 : Math.max(0.001, span(t, 1.9, 2.15, outBack)));
  }, (t) => {
    const k = t > 0.3 ? Math.max(0, settle(t - 0.3, 3, 9)) : 0;
    return [1 + 0.04 * k, 1 - 0.05 * k, 1 + 0.03 * k];
  });

const mangonelHit = (): Clip<MB> =>
  clip<MB>('hit', 0.42, false, (b, t) => {
    const k = settle(t, 2.5, 8) * span(t, 0, 0.03, outQuad);
    const dz = -0.04 * k;
    b.root.pos(0, 0, dz);
    mroll(b, dz / M_WHEEL_R);
    b.frame.rot(-0.05 * k, 0, 0.03 * k);
    b.arm.rot(0.12 * k);
  });

/** Stalled: listing on its wheels, the arm rattling loose. */
const mangonelStun = (): Clip<MB> =>
  clip<MB>('stun', 1.6, true, (b, t) => {
    const p = phase(t, 1.6) * TAU;
    b.frame.rot(0.03 * Math.sin(p + 0.6), 0, 0.05 + 0.03 * Math.sin(p));
    b.arm.rot(0.15 + 0.1 * Math.sin(p - 1.2));
  });

/** It breaks: the skein snaps and the arm flails up and over, a front wheel gives and the carriage slumps nose-down. */
const mangonelDie = (): Clip<MB> =>
  clip<MB>('die', 1.2, false, (b, t) => {
    b.arm.rot(keys(t, [[0, 0], [0.25, 2.4, outQuad], [0.55, 2.9, inQuad], [0.7, 2.75, outQuad], [1.2, 2.85]]));
    b.stone.scale(t < 0.25 ? 1 : 0.001);
    timeline(b, t, [
      [0, {}],
      [0.2, { wheelFL: [0.1, 0, 0.2, -0.02, 0, 0] }],
      [0.6, { wheelFL: [0.3, 0, 1.4, -0.28, -0.17, 0.04] }, inQuad],
      [1.2, { wheelFL: [0.3, 0, 1.5, -0.3, -0.18, 0.05] }],
    ]);
    b.frame.rot(
      keys(t, [[0, 0], [0.2, -0.04], [0.6, 0.12, inQuad], [0.72, 0.08, outQuad], [0.85, 0.13, inQuad], [1.2, 0.125]]),
      0,
      keys(t, [[0, 0], [0.6, 0.18, inQuad], [0.72, 0.14, outQuad], [0.85, 0.19, inQuad], [1.2, 0.18]]),
    ).pos(0, keys(t, [[0, 0], [0.6, -0.09, inQuad], [0.72, -0.06, outQuad], [0.85, -0.1, inQuad], [1.2, -0.1]]), 0);
  });

export const mangonelClips = (): Clip[] => [mangonelIdle(), mangonelWalk(), mangonelAttack(), mangonelHit(), mangonelStun(), mangonelDie()] as Clip[];

/* ---------- archer: a spectral bowman, always nocked ---------- */

type AB = 'root' | 'body' | 'robe' | 'chest' | 'head' | 'mantle' | 'armR' | 'foreR' | 'arrow' | 'strT' | 'strB' | 'legL' | 'shinL' | 'legR' | 'shinR';
type APose = KeyPose<AB>;

const A = ARCHER;
const LEN_UP = A.elbow.distanceTo(A.shoulder);
const LEN_FORE = A.hand.distanceTo(A.elbow);
const DIR_UP = A.elbow.clone().sub(A.shoulder).normalize();
const DIR_FORE = A.hand.clone().sub(A.elbow).normalize();
/** Where the elbow points at rest (the IK pole), perpendicular to shoulder→hand. */
const POLE = (() => {
  const u = A.hand.clone().sub(A.shoulder).normalize();
  const e = A.elbow.clone().sub(A.shoulder);
  return e.sub(u.multiplyScalar(e.dot(u))).normalize();
})();
/** From the nock to the drawing hand. */
const HAND_OFF = A.hand.clone().sub(A.nock);

const _u = new THREE.Vector3();
const _v = new THREE.Vector3();
const _e = new THREE.Vector3();
const _h = new THREE.Vector3();
const _qa = new THREE.Quaternion();
const _qf = new THREE.Quaternion();
const _eu = new THREE.Euler();

/** Bends the drawing arm so its hand reaches `hand` (chest frame); `lift` raises the elbow (a deep draw). */
const reach = (b: Bones<AB>, hand: THREE.Vector3, lift = 0): void => {
  const d = _u.subVectors(hand, A.shoulder);
  const dist = Math.min(LEN_UP + LEN_FORE - 1e-3, Math.max(Math.abs(LEN_UP - LEN_FORE) + 1e-3, d.length()));
  d.normalize();
  const pole = _v.copy(POLE).add(_e.set(0.15 * lift, lift, -0.6 * lift));
  pole.sub(_e.copy(d).multiplyScalar(pole.dot(d))).normalize();
  const x = (LEN_UP * LEN_UP - LEN_FORE * LEN_FORE + dist * dist) / (2 * dist);
  const h = Math.sqrt(Math.max(0, LEN_UP * LEN_UP - x * x));
  const elbow = _e.copy(A.shoulder).addScaledVector(d, x).addScaledVector(pole, h);
  const wrist = _h.copy(A.shoulder).addScaledVector(d, dist);
  _qa.setFromUnitVectors(DIR_UP, _v.subVectors(elbow, A.shoulder).normalize());
  _qf.setFromUnitVectors(DIR_FORE, _v.subVectors(wrist, elbow).normalize());
  _qf.premultiply(_qa.clone().invert());
  _eu.setFromQuaternion(_qa, 'XYZ');
  b.armR.rot(_eu.x, _eu.y, _eu.z);
  _eu.setFromQuaternion(_qf, 'XYZ');
  b.foreR.rot(_eu.x, _eu.y, _eu.z);
};

/** Pulls both halves of the string to a nock at depth `z` (chest frame; 0.34 at rest, 0.395 braced straight). */
const string = (b: Bones<AB>, z: number): void => {
  for (const [bone, tip] of [[b.strT, A.top], [b.strB, A.bot]] as const) {
    const y0 = A.nock.y - tip.y, z0 = A.nock.z - tip.z, z1 = z - tip.z;
    bone.rot(Math.atan2(z1, y0) - Math.atan2(z0, y0)).scale(Math.hypot(y0, z1) / Math.hypot(y0, z0));
  }
};

/** The arrow's nock at `at` (chest frame), turned `rx` (−π/2 points it up), at size `s`. */
const arrowAt = (b: Bones<AB>, at: THREE.Vector3, rx = 0, s = 1): void => {
  b.arrow.pos(at.x - A.nock.x, at.y - A.nock.y, at.z - A.nock.z).rot(rx).scale(Math.max(1e-3, s));
};

/** Holds the string at depth `z` with the arrow on it and the hand on the nock. */
const hold = (b: Bones<AB>, z: number, lift = 0): void => {
  const n = new THREE.Vector3(A.nock.x, A.nock.y, z);
  string(b, z);
  arrowAt(b, n);
  reach(b, n.add(HAND_OFF), lift);
};

const archerIdle = (): Clip<AB> =>
  clip<AB>('idle', 4, true, (b, t) => {
    const p = phase(t, 4) * TAU;
    const br = Math.sin(p * 2);
    // A ghost's stillness: it barely breathes, the aim drifts slowly from target to target.
    b.body.pos(0, 0.01 * br, 0).rot(0, 0.04 * Math.sin(p), 0);
    b.chest.rot(-0.02 * br, 0.04 * Math.sin(p + 0.5), 0.015 * Math.sin(p));
    b.head.rot(0.03 * br, -0.03 * Math.sin(p + 0.5) + 0.06 * Math.sin(p * 2 + 0.3), 0.04 * Math.sin(p + 1));
    // The string creeps back and eases, twice a loop.
    hold(b, A.nock.z - 0.035 * (0.5 - 0.5 * Math.cos(p * 2 - 0.5)));
    // Cloth drifting as if under water.
    b.robe.rot(0.03 * Math.sin(p - 0.8) + 0.015 * Math.sin(p * 3), 0.04 * Math.sin(p), 0.035 * Math.sin(p - 1.3));
    b.mantle.rot(0.03 * Math.sin(p * 2 - 1), 0.02 * Math.sin(p - 0.4), 0.025 * Math.sin(p - 1.6));
    b.legL.rot(0, 0, -0.012 * Math.sin(p));
    b.legR.rot(0, 0, -0.012 * Math.sin(p));
  });

/** A gliding stride with the bow held at aim; the robe trails and drifts. */
const archerWalk = (dur = 0.96): Clip<AB> =>
  clip<AB>('walk', dur, true, (b, t) => {
    const th = phase(t, dur) * TAU;
    const sn = Math.sin(th), cs = Math.cos(th);
    b.legL.rot(-0.42 * sn - 0.03);
    b.legR.rot(0.42 * sn - 0.03);
    b.shinL.rot(0.8 * Math.max(0, cs) ** 1.5 + 0.05);
    b.shinR.rot(0.8 * Math.max(0, -cs) ** 1.5 + 0.05);
    b.root.pos(0, 0.02 * Math.cos(2 * th) - 0.012, 0).rot(0, 0, 0.018 * sn);
    b.body.rot(0.04, -0.08 * sn, 0);
    // The chest turns back against the hips: the aim stays steady.
    b.chest.rot(-0.012 * Math.cos(2 * th - 0.4), 0.075 * sn, -0.012 * sn);
    b.head.rot(0.01 * Math.cos(2 * th - 0.8), 0.01 * sn, 0);
    hold(b, A.nock.z);
    b.robe.rot(0.13 + 0.05 * Math.cos(2 * th - 1.4), 0.05 * Math.sin(th - 1.2), 0.045 * Math.sin(th - 1.3));
    b.mantle.rot(0.06 + 0.03 * Math.cos(2 * th - 1.8), 0, 0.025 * Math.sin(th - 1.5));
  });

/** Draw to the cheek, loose at 0.13 s, the hand flies back; it reaches over its shoulder for another arrow and nocks it. */
const archerAttack = (): Clip<AB> =>
  clip<AB>('attack', 0.82, false, (b, t) => {
    const drawn = A.nock.z - 0.27;
    const drawZ = keys(t, [[0, A.nock.z], [0.1, drawn, outQuad], [0.13, drawn - 0.01]]);
    timeline(b, t, [
      [0, {}],
      [0.1, { chest: [-0.05, 0.12, 0], head: [0.08, -0.1, -0.08], body: [0, 0.05, 0], legL: [-0.12, 0, -0.03], legR: [0.14, 0, 0.03], mantle: [-0.03, 0, 0] }, outQuad],
      [0.13, { chest: [-0.06, 0.13, 0], head: [0.08, -0.1, -0.08], body: [0, 0.05, 0], legL: [-0.12, 0, -0.03], legR: [0.14, 0, 0.03], mantle: [-0.03, 0, 0] }],
      [0.3, { chest: [-0.02, 0.06, 0], head: [0.04, -0.06, -0.04], body: [0, 0.03, 0], legL: [-0.1, 0, -0.03], legR: [0.12, 0, 0.03] }, outQuad],
      // Reaching over the shoulder: the chest turns, the head follows the hand a little.
      [0.46, { chest: [-0.05, 0.18, 0.04], head: [-0.05, 0.18, 0], body: [0, 0.06, 0], legL: [-0.08, 0, -0.02], legR: [0.1, 0, 0.02] }, inOut],
      [0.82, {}, inOut],
    ], { head: 0.025, mantle: 0.05 });
    // The loose kicks the bow arm (the whole chest) and sets the cloth fluttering.
    if (t > 0.13) {
      const k = settle(t - 0.13, 4, 9);
      b.chest.rot(0.04 * k, -0.08 * k, 0.02 * k);
      b.robe.rot(0.06 * k);
      b.mantle.rot(-0.1 * k, 0, 0.05 * k);
    }
    let z: number;
    const hand = new THREE.Vector3();
    if (t <= 0.13) {
      z = drawZ;
      hold(b, z, span(t, 0.02, 0.1));
      return;
    }
    // Loosed: the string snaps forward past straight and hums; it takes the new arrow from 0.66 s.
    const snap = lerp(drawZ, 0.395, span(t, 0.13, 0.155, outExpo));
    z = snap + (t > 0.155 ? 0.018 * settle(t - 0.155, 9, 11) : 0);
    z = lerp(z, A.nock.z, span(t, 0.66, 0.82));
    string(b, z);
    // The hand flies back past the ear, reaches the quiver, draws an arrow and brings it to the string.
    const hx: Key[] = [[0.13, HAND_OFF.x], [0.2, 0.13, outExpo], [0.3, 0.12], [0.44, 0.2, inOut], [0.52, 0.18], [0.66, HAND_OFF.x, inOut]];
    const hy: Key[] = [[0.13, A.nock.y], [0.2, 1.48, outExpo], [0.3, 1.47], [0.44, 1.63, inOut], [0.52, 1.72, outQuad], [0.66, A.nock.y, inOut]];
    const hz: Key[] = [[0.13, drawn - 0.01 + HAND_OFF.z], [0.2, -0.06, outExpo], [0.3, -0.04], [0.44, -0.2, inOut], [0.52, -0.12], [0.66, 0.395 + HAND_OFF.z, inOut]];
    hand.set(keys(t, hx), keys(t, hy), keys(t, hz));
    if (t > 0.66) hand.z = z + HAND_OFF.z;
    reach(b, hand, 0.6 * (1 - span(t, 0.13, 0.3)) + 0.5 * span(t, 0.3, 0.44) * (1 - span(t, 0.52, 0.66)));
    // The shot arrow is gone; a new one appears in the hand at the quiver, pointing up, and swings down onto the string.
    const s = span(t, 0.44, 0.5);
    const nock = hand.clone().sub(HAND_OFF);
    if (t > 0.66) nock.set(A.nock.x, A.nock.y, z);
    arrowAt(b, nock, t < 0.44 ? 0 : keys(t, [[0.44, -1.5], [0.52, -1.3], [0.66, 0, inOut]]), s);
  });

const archerHit = (): Clip<AB> =>
  clip<AB>('hit', 0.4, false, (b, t) => {
    const k = settle(t, 2.4, 8) * span(t, 0, 0.03, outQuad);
    const a = Math.abs(k);
    hold(b, A.nock.z);
    b.body.rot(-0.2 * k, 0.1 * k, 0);
    b.chest.rot(-0.22 * k, 0.12 * k, 0.06 * k);
    b.head.rot(-0.4 * k, -0.15 * k, 0.05 * k);
    b.root.pos(0, -0.02 * a, -0.07 * k);
    b.shinL.rot(0.15 * a);
    b.shinR.rot(0.2 * a);
    const lag = settle(t - 0.04, 2, 6) * span(t, 0.02, 0.06);
    b.robe.rot(0.3 * lag, 0, 0.08 * lag);
    b.mantle.rot(0.25 * lag);
  }, (t) => {
    const k = Math.max(0, settle(t, 3, 9));
    return [1 + 0.06 * k, 1 - 0.07 * k, 1 + 0.06 * k];
  });

/** Dazed: the bow sags, the hood rolls, the ghost wavers on its feet. */
const archerStun = (): Clip<AB> =>
  clip<AB>('stun', 1.6, true, (b, t) => {
    const p = phase(t, 1.6) * TAU;
    hold(b, 0.38);
    b.root.rot(0.04 * Math.sin(p), 0, 0.07 * Math.cos(p));
    b.body.rot(0.08 + 0.04 * Math.sin(p * 2), 0, -0.06 * Math.cos(p));
    b.chest.rot(0.28 + 0.06 * Math.sin(p + 0.6), 0.1 * Math.sin(p + 0.4), 0);
    b.head.rot(0.25 + 0.2 * Math.sin(p + 0.9), 0.3 * Math.cos(p + 0.9), 0.2 * Math.sin(p + 0.9));
    b.legL.rot(-0.05, 0, 0);
    b.shinL.rot(0.12);
    b.shinR.rot(0.18);
    b.robe.rot(0.06 * Math.sin(p - 1), 0, 0.08 * Math.cos(p - 1.2));
    b.mantle.rot(0.05 * Math.sin(p - 1.4), 0, 0.06 * Math.cos(p - 1.5));
  });

/** It jerks, the arrow's light gutters out, the knees fold and the ghost crumples forward over its bow. */
const archerDie = (): Clip<AB> =>
  clip<AB>('die', 1.1, false, (b, t) => {
    const slack = span(t, 0.1, 0.25);
    const z = lerp(A.nock.z, 0.395, slack);
    string(b, z);
    reach(b, new THREE.Vector3(A.nock.x, A.nock.y, z).add(HAND_OFF).add(_h.set(0.04 * slack, -0.18 * slack, -0.04 * slack)));
    arrowAt(b, new THREE.Vector3(A.nock.x, A.nock.y - 0.05 * slack, z), 0.25 * slack, 1 - span(t, 0.15, 0.45));
    const down: APose = { body: [0.35, 0, 0.05], chest: [0.3, 0.1, 0.1], head: [0.35, 0.3, -0.35], robe: [-0.15, 0, 0.3], mantle: [-0.1, 0, 0.15], legL: [-0.8, 0, 0.05], shinL: [1.1, 0, 0], legR: [-0.6, 0, 0.1], shinR: [1.2, 0, 0], root: [0.1, 0, 1.38, 0, 0.1, 0.05] };
    timeline(b, t, [
      [0, {}],
      [0.1, { body: [-0.25, 0.1, 0], chest: [-0.3, 0.1, 0.05], head: [-0.55, 0.15, 0], robe: [0.25, 0, 0], mantle: [0.2, 0, 0], root: [0, 0, 0, 0, 0.03, -0.05] }, outExpo],
      [0.42, { body: [0.15, 0, 0.05], chest: [0.3, 0.1, 0.05], head: [0.35, 0.25, 0.1], robe: [0.1, 0, 0], legL: [-1.3, 0, -0.15], shinL: [2.0, 0, 0], legR: [-1.2, 0, 0.15], shinR: [2.0, 0, 0], root: [0, 0, 0, 0, -0.38, 0] }, inQuad],
      [0.78, down, inQuad],
      [1.1, { ...down, root: [0.1, 0, 1.42, 0, 0.1, 0.05] }, outBack],
    ], { head: 0.03, robe: 0.06, mantle: 0.05 });
  });

export const archerClips = (): Clip[] => [archerIdle(), archerWalk(), archerAttack(), archerHit(), archerStun(), archerDie()] as Clip[];

/* ---------- pumpkin: a lantern-headed scarecrow of vines ---------- */

type PB = 'root' | 'body' | 'cloak' | 'head' | 'armL' | 'foreL' | 'armR' | 'foreR' | 'legL' | 'shinL' | 'legR' | 'shinR';
type PP = KeyPose<PB>;

/** Stooped, the lantern hanging forward on its neck, vine arms loose. */
const P_STANCE: PP = {
  body: [0.1, 0, 0], head: [0.1, 0, 0], armL: [-0.1, 0, -0.04], armR: [-0.1, 0, 0.04], foreL: [-0.15, 0, 0], foreR: [-0.15, 0, 0],
  legL: [-0.08, 0, 0], shinL: [0.12, 0, 0], legR: [-0.08, 0, 0], shinR: [0.12, 0, 0], root: [0, 0, 0, 0, -0.01, 0],
};

const pumpkinIdle = (): Clip<PB> =>
  clip<PB>('idle', 3.2, true, (b, t) => {
    const ph = phase(t, 3.2);
    const p = ph * TAU;
    const br = Math.sin(p * 2);
    apply(b, P_STANCE);
    b.body.rot(0.03 * br, 0.06 * Math.sin(p), 0.035 * Math.sin(p + 0.4));
    b.root.pos(0, -0.008 * (1 + br), 0);
    // The head cocks slowly to one side, studying you, then snaps back.
    const cock = keys(ph, [[0, 0], [0.38, 1, inOut], [0.44, -0.25, outExpo], [0.7, -0.15], [1, 0, inOut]]);
    b.head.rot(0.04 * br + 0.05 * Math.max(0, cock), 0.22 * cock, 0.2 * cock);
    // Arms dangle and sway a beat behind the body; the twig claws twitch.
    b.armL.rot(0.05 * Math.sin(p - 0.6), 0, -0.03 * Math.sin(p - 0.5));
    b.armR.rot(0.05 * Math.sin(p - 0.9), 0, -0.03 * Math.sin(p - 0.5));
    b.foreL.rot(-0.08 * Math.sin(p - 1.2) - 0.18 * Math.max(0, Math.sin(p * 3 + 1)) ** 10);
    b.foreR.rot(-0.08 * Math.sin(p - 1.5) - 0.18 * Math.max(0, Math.sin(p * 3 - 0.6)) ** 10);
    b.cloak.rot(0.04 + 0.03 * Math.sin(p * 2 - 1), 0.03 * Math.sin(p - 0.5), 0.04 * Math.sin(p - 1.1));
    b.legL.rot(0, 0, -0.015 * Math.sin(p));
    b.legR.rot(0, 0, -0.015 * Math.sin(p));
  }, (t) => {
    const br = Math.sin(phase(t, 3.2) * TAU * 2);
    return [1 + 0.012 * br, 1 - 0.015 * br, 1 + 0.012 * br];
  });

/** A lurching shamble: the left leg drags, the body heaves from side to side, the lantern lolls behind. */
const pumpkinWalk = (dur = 1.0): Clip<PB> =>
  clip<PB>('walk', dur, true, (b, t) => {
    const th = phase(t, dur) * TAU;
    const sn = Math.sin(th), cs = Math.cos(th);
    apply(b, P_STANCE);
    b.legL.rot(-0.36 * sn + 0.04, 0, -0.03);
    b.legR.rot(0.5 * sn, 0, 0.03);
    b.shinL.rot(0.45 * Math.max(0, cs) ** 1.5 + 0.06);
    b.shinR.rot(0.9 * Math.max(0, -cs) ** 1.5);
    b.root.pos(0, 0.03 * Math.cos(2 * th) - 0.015, 0).rot(0, 0.06 * sn, 0.08 * Math.sin(th - 0.3));
    b.body.rot(0.1 + 0.05 * Math.cos(2 * th - 0.6), -0.1 * sn, -0.06 * sn);
    b.head.rot(-0.04 + 0.08 * Math.cos(2 * th - 1.2), 0.12 * Math.sin(th - 0.9), 0.14 * Math.sin(th - 1.1));
    b.armL.rot(0.3 * Math.sin(th - 0.7) - 0.08, 0, -0.06);
    b.armR.rot(-0.36 * Math.sin(th - 0.7) - 0.08, 0, 0.06);
    b.foreL.rot(-0.06 - 0.25 * Math.max(0, Math.sin(th - 1.4)));
    b.foreR.rot(-0.06 - 0.25 * Math.max(0, -Math.sin(th - 1.4)));
    b.cloak.rot(0.12 + 0.05 * Math.cos(2 * th - 1.6), 0.05 * Math.sin(th - 1.5), 0.06 * Math.sin(th - 1.6));
  }, (t) => {
    const k = Math.cos(phase(t, dur) * TAU * 2);
    return [1 - 0.02 * k, 1 + 0.025 * k, 1 - 0.02 * k];
  });

/** Rears up, both claws high and the lantern flaring, then lunges and rakes down; the claws land at 0.13 s. */
const pumpkinAttack = (): Clip<PB> =>
  clip<PB>('attack', 0.62, false, (b, t) => {
    const strike: PP = { ...P_STANCE, armL: [-0.5, 0, 0.25], armR: [-0.4, 0, -0.25], foreL: [-0.1, 0, 0], foreR: [-0.1, 0, 0], body: [0.6, 0.1, 0], head: [-0.3, 0, 0, 0, 0, 0, 1.14], legL: [-0.55, 0, 0], shinL: [0.45, 0, 0], legR: [0.25, 0, 0], shinR: [0.15, 0, 0], root: [0, 0, 0, 0, -0.06, 0.14], cloak: [0.25, 0, 0] };
    timeline(b, t, [
      [0, P_STANCE],
      [0.07, { ...P_STANCE, armL: [-2.3, 0, -0.4], armR: [-2.4, 0, 0.4], foreL: [-0.6, 0, 0], foreR: [-0.6, 0, 0], body: [-0.25, -0.1, 0], head: [-0.3, 0, 0, 0, 0, 0, 1.06], legR: [0.15, 0, 0], root: [0, 0, 0, 0, 0.02, -0.04], cloak: [-0.15, 0, 0] }, outQuad],
      [0.13, strike, inCubic],
      [0.28, { ...strike, armL: [-0.25, 0, 0.3], armR: [-0.2, 0, -0.3], body: [0.55, 0.1, 0], head: [-0.25, 0, 0, 0, 0, 0, 1.08], cloak: [0.15, 0, 0] }, outBack],
      [0.62, P_STANCE, inOut],
    ], { foreL: 0.012, foreR: 0.02, head: 0.025, cloak: 0.06 });
  }, (t) => {
    const k = t > 0.13 ? Math.max(0, settle(t - 0.13, 3, 10)) : 0;
    return [1 + 0.04 * k, 1 - 0.05 * k, 1 + 0.04 * k];
  });

const pumpkinHit = (): Clip<PB> =>
  clip<PB>('hit', 0.4, false, (b, t) => {
    apply(b, P_STANCE);
    const k = settle(t, 2.4, 8) * span(t, 0, 0.03, outQuad);
    const a = Math.abs(k);
    b.body.rot(-0.35 * k, 0.15 * k, 0.05 * k);
    // The heavy lantern whips back on its thin neck and wobbles.
    const w = settle(t - 0.02, 3, 7) * span(t, 0, 0.05);
    b.head.rot(-0.5 * w, -0.2 * w, 0.25 * w);
    b.armL.rot(-0.4 * k, 0, -0.3 * k);
    b.armR.rot(-0.35 * k, 0, 0.3 * k);
    b.foreL.rot(-0.3 * a);
    b.foreR.rot(-0.3 * a);
    b.root.pos(0, -0.02 * a, -0.08 * k);
    b.shinL.rot(0.2 * a);
    b.shinR.rot(0.25 * a);
    b.cloak.rot(0.3 * settle(t - 0.05, 2, 6) * span(t, 0.03, 0.07));
  }, (t) => {
    const k = Math.max(0, settle(t, 3, 9));
    return [1 + 0.08 * k, 1 - 0.1 * k, 1 + 0.08 * k];
  });

/** Dazed: the lantern rolls round on its neck, the arms hang limp, the body sways. */
const pumpkinStun = (): Clip<PB> =>
  clip<PB>('stun', 1.6, true, (b, t) => {
    const p = phase(t, 1.6) * TAU;
    apply(b, P_STANCE, 0.6);
    b.root.rot(0.05 * Math.sin(p), 0, 0.09 * Math.cos(p));
    b.body.rot(0.18 + 0.06 * Math.sin(p * 2), 0, -0.08 * Math.cos(p));
    b.head.rot(0.3 * Math.sin(p + 0.9), 0.45 * Math.cos(p + 0.9), 0.3 * Math.sin(p + 0.9));
    b.armL.rot(0.15 * Math.sin(p + 1.4), 0, 0.05);
    b.armR.rot(-0.15 * Math.sin(p + 1.4), 0, -0.05);
    b.foreL.rot(0.1);
    b.foreR.rot(0.1);
    b.cloak.rot(0.06 * Math.sin(p - 1), 0, 0.08 * Math.cos(p - 1.2));
  });

/**
 * It convulses, the lantern swelling as the fire inside builds (the view bursts it), then the vine body
 * folds and the lantern drops off its neck and rolls on the ground.
 */
const pumpkinDie = (): Clip<PB> =>
  clip<PB>('die', 1.1, false, (b, t) => {
    const fallen: PP = { body: [0.15, 0, 0.1], head: [-1.2, 0.4, 1.3, 0.06, 0.3, -0.1], cloak: [0.25, 0, 0.1], armL: [-2.2, 0, -0.35], foreL: [-0.25, 0, 0], armR: [-1.9, 0, 0.45], foreR: [-0.35, 0, 0], legL: [-0.35, 0, -0.15], shinL: [0.6, 0, 0], legR: [-0.25, 0, 0.15], shinR: [0.7, 0, 0], root: [1.3, 0, 0, 0, 0.06, 0.05] };
    timeline(b, t, [
      [0, P_STANCE],
      [0.08, { ...P_STANCE, body: [-0.4, 0.1, 0], head: [-0.5, 0.2, 0, 0, 0, 0, 1.1], armL: [-0.6, 0, -1.1], armR: [-0.6, 0, 1.1], foreL: [-0.5, 0, 0], foreR: [-0.5, 0, 0], cloak: [0.3, 0, 0], root: [0, 0, 0, 0, 0.02, -0.04] }, outExpo],
      [0.34, { ...P_STANCE, body: [-0.1, 0, 0], head: [-0.2, 0, 0, 0, 0, 0, 1.28], armL: [-0.4, 0, -0.9], armR: [-0.4, 0, 0.9], foreL: [-0.7, 0, 0], foreR: [-0.7, 0, 0], cloak: [0.2, 0, 0] }, inOut],
      [0.56, { body: [0.5, 0, 0.05], head: [0.3, 0.2, 0.2, 0, -0.05, 0.05, 1.05], armL: [-0.2, 0, -0.2], armR: [-0.2, 0, 0.2], legL: [-1.2, 0, -0.15], shinL: [1.9, 0, 0], legR: [-1.1, 0, 0.15], shinR: [1.9, 0, 0], root: [0, 0, 0, 0, -0.26, 0] }, inQuad],
      [0.84, fallen, inQuad],
      [1.1, { ...fallen, head: [-1.2, 0.55, 1.5, 0.07, 0.32, -0.1] }, outQuad],
    ], { head: 0.03, cloak: 0.06, foreL: 0.02, foreR: 0.02 });
    // The convulsion: a hard tremor while the fire swells.
    const tr = span(t, 0.08, 0.14) * (1 - span(t, 0.4, 0.5));
    b.body.rot(0.06 * tr * Math.sin(t * 70), 0, 0.05 * tr * Math.sin(t * 57));
    b.head.rot(0.08 * tr * Math.sin(t * 63 + 1), 0.1 * tr * Math.sin(t * 49), 0);
    b.foreL.rot(0.2 * tr * Math.sin(t * 66));
    b.foreR.rot(0.2 * tr * Math.sin(t * 61 + 2));
  });

export const pumpkinClips = (): Clip[] => [pumpkinIdle(), pumpkinWalk(), pumpkinAttack(), pumpkinHit(), pumpkinStun(), pumpkinDie()] as Clip[];

/** Clips by library kind (see ../rigs.ts). */
export const CLIPS: Record<string, () => Clip[]> = {
  sapper: sapperClips,
  ram: ramClips,
  mangonel: mangonelClips,
  archer: archerClips,
  pumpkin: pumpkinClips,
};
